// © Microsoft Corporation. All rights reserved.

import { BreakpointManager, BreakpointManagerCallback, ColumnArrangement, columnArrangements, updateColumnArrangementsForFiveCol } from "./BreakpointManager";
import { BreakpointTracker, BreakpointTrackerCallback, defaultBreakpoints } from "@microsoft/fast-layouts-react";
import { IRequestContext, getContextData } from "@msnews/core";

import { Breakpoint } from "@microsoft/fast-layouts-react";
import { pull as _pull } from "lodash-es";

/**
 * This class establishes a standard breakpoint scheme that can be used by all experiences
 * to achieve a unified breakpoint strategy. The implementation is a glorified wrapper around
 * the FAST BreakpointTracker that standardizes on a set of specific number of column arrangements
 * that will then be exposed to clients via the {ColumnArrangement} enum.
 */
export class FastBreakpointManager implements BreakpointManager {

    /**
     * Map of BreakpointManager callbacks to Breakpoint Tracker callback.
     * Required to quickly get Breakpoint callback on unsubscribe.
     */
    private callbackMap: Map<BreakpointManagerCallback, BreakpointTrackerCallback> = new Map<BreakpointManagerCallback, BreakpointTrackerCallback>();

    /**
     * Gets the {ColumnArrangement} enum that maps to the current viewport width.
     */
    public get currentColumnArrangement(): ColumnArrangement {
        return this.indexToStandardConverter(BreakpointTracker.currentBreakpoint());
    }

    /**
     * Set a new default breakpoint, default to 0 if no requestContext provided
     * @param requestContext: used to access the current request's breakpoint
     */
    public constructor(requestContext: IRequestContext) {
        this.refreshBreakpoints(requestContext);
    }

    /**
     * Refreshes breakpoints from current column arrangements.
     */
    public refreshBreakpoints(requestContext: IRequestContext): void {
        const breakpoints: number[] = [0];
        updateColumnArrangementsForFiveCol();
        for (let i = 1; i < columnArrangements.length; i++) {
            breakpoints.push(columnArrangements[i].minViewportWidthPx);
        }

        BreakpointTracker.breakpoints = breakpoints;
        BreakpointTracker.defaultBreakpoint = requestContext !== undefined ?
            getContextData(requestContext.data.breakpoint, "breakpoint") : 0;
    }

    /** breakpoint values */
    public set breakpoints(breakpoints: number[]) {
        BreakpointTracker.breakpoints = breakpoints;

        // TODO: Remove this when https://github.com/microsoft/fast-dna/issues/2602 is solved
        if (defaultBreakpoints && Array.isArray(defaultBreakpoints)) {
            const count = defaultBreakpoints.length;
            for (let i = 0; i < count; i++) {
                defaultBreakpoints.pop();
            }
            breakpoints.forEach(breakpoint => defaultBreakpoints.push(breakpoint));
        }
    }

    public get breakpoints(): number[] {
        return BreakpointTracker.breakpoints;
    }

    /**
     * Clear all callbacks. Useful when handling multiple requests when server side rendering to prevent memory leak.
     */
    public static resetCallbacks(): void {
        if (_fastBreakpointManager && _fastBreakpointManager.callbackMap.size > 0) {
            for (const [key, _] of _fastBreakpointManager.callbackMap) {
                _fastBreakpointManager.unsubscribe(key);
                _fastBreakpointManager.callbackMap.delete(key);
            }
        }
    }

    /**
     * Add a subscription to the breakpoint tracker changed notification list
     * @param callback - the callback to invoke when the breakpoint changes
     */
    public subscribe(callback: BreakpointManagerCallback): void {

        // Create callback with breakpoint value conversion.
        const managerCallback = (breakpoint: Breakpoint) => {
            callback(this.indexToStandardConverter(breakpoint));
        };

        this.callbackMap.set(callback, managerCallback);

        BreakpointTracker.subscribe(managerCallback);
    }

    /**
     * Removes an existing subscription from the breakpoint tracker notification list
     * @param callback - the callback to remove from the notification list
     */
    public unsubscribe(callback: BreakpointManagerCallback): void {
        BreakpointTracker.unsubscribe(this.callbackMap.get(callback));
        this.callbackMap.delete(callback);
    }

    /**
     * Converts an index-based breakpoint to a column arrangement.
     * @param breakpoint The breakpoint index to convert.
     * @returns The corresponding column arrangement.
     */
    private indexToStandardConverter(breakpoint: Breakpoint): ColumnArrangement {
        return columnArrangements[breakpoint].arrangement;
    }
}

let _fastBreakpointManager: FastBreakpointManager;

/**
 * Default instance of fastBreakpointManager
 * @param requestContext - The current request context
 * @param recreate - Flag specifying if we should create a new instance of fastBreakpointManager
 */
export const fastBreakpointManager = (requestContext?: IRequestContext, recreate?: boolean) => {
    if (!_fastBreakpointManager || recreate) {
        // First clear any subscribed to events from the callback map
        FastBreakpointManager.resetCallbacks();
        _fastBreakpointManager = new FastBreakpointManager(requestContext);
    }

    return _fastBreakpointManager;
};

let viewType: string = null;

/**
  * To get view type for Edgenext based on river column view
  */
export const viewTypeForEdgeNext = () => {
    switch (fastBreakpointManager().currentColumnArrangement) {
        case ColumnArrangement.c1:
        case ColumnArrangement.c2:
            viewType = "size2column";
            break;
        case ColumnArrangement.c3:
            viewType = "size3column";
            break;
        case ColumnArrangement.c4:
            viewType = "size4column";
            break;
        case ColumnArrangement.c5:
            viewType = "size5column";
            break;
        default:
            // leave EdgeNextTelemetry.viewType unchanged
            break;
    }
    return viewType;
};
